import { useEffect, useState } from 'react';

import { OutlookRead } from '../abstractions/Outlook';
import { getReadItem } from '../abstractions/item';
import {
  getMailbox,
  isViewInReadMode,
  itemIsMessageRead,
  canSkipTokenForFiltering,
} from '../abstractions/office';
import { OfficeError } from '../errors';
import { LOG_TYPE, MetricService } from '../services/MetricService';
import { getSettingsService } from '../services/SettingsService';
import { getOfficeErrorFromError } from '../errors/OfficeError';

// the "pointless" line is used to get a generic return type so we don't have
// to type it by hand.
/* eslint-disable-next-line react-hooks/rules-of-hooks */ /* istanbul ignore next */
const pointless = () => useState('');
type StringState = ReturnType<typeof pointless>;

type MailFolder = {
  id: string;
  displayName: string;
};

export function useFilteredTracking(): StringState {
  const [filterItem, setFilterItem] = useState('');
  const settings = getSettingsService('bombbomb');
  useEffect(() => {
    function extractFromAddress() {
      const item = getReadItem();
      if (itemIsMessageRead(item)) {
        setFilterItem(item.from.emailAddress ?? '');
      }
    }

    function extractToAddress() {
      const item = getReadItem();
      if (itemIsMessageRead(item)) {
        const toAddresses = item.to;
        if (toAddresses.length === 1) {
          setFilterItem(item.to[0].emailAddress ?? '');
        } else if (toAddresses.length > 1) {
          extractSubject(item);
        }
      }
    }

    function extractSubject(item: ReturnType<typeof getReadItem>) {
      setFilterItem(item.subject ?? '');
    }

    async function TokenlessCheckIfInSent() {
      const userEmail = Office.context.mailbox.userProfile.emailAddress;
      const item = getReadItem();
      let fromEmail = null;
      if (item && item.from) {
        fromEmail = item.from.emailAddress;
      }

      try {
        if (fromEmail && fromEmail.toLowerCase() !== userEmail.toLowerCase()) {
          extractFromAddress();
        } else {
          extractToAddress();
        }
      } catch (error) {
        MetricService.log(
          'error checking if item is in sent folder during Tokenless flow.',
          LOG_TYPE.ERROR,
          getOfficeErrorFromError(error),
        );
      }
    }

    async function checkIfInSent() {
      if (isViewInReadMode()) {
        if (!settings.get('filterTracking')) {
          return;
        }
        try {
          // Outlook REST APIs are deprecated. Use tokenless approach when possible.
          if (canSkipTokenForFiltering()) {
            TokenlessCheckIfInSent();
            return;
          }
          const item = getReadItem();
          const outlook = new OutlookRead(item);
          const parentFolderId = await outlook.getCurrentParentFolder();

          const mailFolders = localStorage.getItem('mailFolders');
          if (mailFolders) {
            const parsedMailFolders = JSON.parse(mailFolders) as MailFolder[];
            const [folder] = parsedMailFolders.filter((folder) => folder.id === parentFolderId);
            if (folder?.displayName === 'Sent Items') {
              extractToAddress();
            } else if (folder?.displayName === 'Inbox') {
              extractFromAddress();
            }
          }
        } catch (error) {
          MetricService.log(
            'error checking if item is in sent folder',
            LOG_TYPE.ERROR,
            getOfficeErrorFromError(error),
          );
        }
      } else {
        setFilterItem('');
      }
    }
    const mailbox = getMailbox();

    mailbox.addHandlerAsync(Office.EventType.ItemChanged, checkIfInSent, {}, (result) => {
      try {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          checkIfInSent();
          MetricService.increment('livefire.attach_item_watch.success');
        } else {
          MetricService.log(
            'error attaching ItemChanged handler',
            LOG_TYPE.ERROR,
            new OfficeError(result.error),
          );
          MetricService.increment('livefire.attach_item_watch.fail');
        }
      } catch (error) {
        MetricService.log(
          'error attaching ItemChanged handler',
          LOG_TYPE.ERROR,
          getOfficeErrorFromError(error),
        );
        MetricService.increment('livefire.attach_item_watch.fail');
      }
    });

    return function unhookItemChangeEvent() {
      mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
    };
  }, [settings]);

  return [filterItem, setFilterItem];
}
